// @ts-check

/** @file Renders the timelog dashboard date-range modal. */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {memo, useEffect} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Pressable, Text, TextInput, View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"

import Modal from "../../../components/modal"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TimelogsDashboardDateRangeModalProps
 * @property {string} [dateFrom] Current start date filter.
 * @property {string} [dateTo] Current end date filter.
 * @property {boolean} isVisible Whether the modal is visible.
 * @property {(values: {dateFrom: string, dateTo: string}) => unknown} onApply Apply callback.
 * @property {() => unknown} onClose Close callback.
 */

/**
 * @typedef {object} TimelogsDashboardDateRangeModalState
 * @property {string} draftDateFrom Draft start date.
 * @property {string} draftDateTo Draft end date.
 */

/**
 * Displays date range controls for timelog filters.
 * @augments {ShapeComponent<TimelogsDashboardDateRangeModalProps, TimelogsDashboardDateRangeModalState>}
 */
class TimelogsDashboardDateRangeModal extends ShapeComponent {
  /** @type {TimelogsDashboardDateRangeModalState} */
  state = {
    draftDateFrom: "",
    draftDateTo: ""
  }
  static propTypes = PropTypesExact({
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
    isVisible: PropTypes.bool.isRequired,
    onApply: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
  })
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /**
   * Synchronizes draft fields when the modal opens.
   * @returns {void} No return value.
   */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()

    const dateFrom = this.p.dateFrom
    const dateTo = this.p.dateTo
    const isVisible = this.p.isVisible
    useEffect(() => {
      if (!isVisible) return
      this.s.draftDateFrom = dateFrom || ""
      this.s.draftDateTo = dateTo || ""
    }, [dateFrom, dateTo, isVisible])
  }

  /**
   * Renders the date range modal shell.
   * @returns {ReactNode} Modal content.
   */
  render() {
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <Modal
        cardStyle={styles.card ||= {
          gap: 16,
          maxWidth: 440,
          padding: 20
        }}
        isVisible={this.p.isVisible}
        onRequestClose={this.p.onClose}
        testID="timelogsDashboardScreen/dateRangeModal"
      >
          <Text
            style={styles[`title-${themeName}`] ||= {
              color: colors.text,
              fontSize: 20,
              fontWeight: 700
            }}
            testID="timelogsDashboardScreen/dateRangeModal/title"
          >
            {_("Date range")}
          </Text>
          <View
            style={styles.fieldsRow ||= {
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 12
            }}
            testID="timelogsDashboardScreen/dateRangeModal/fields"
          >
            <View
              style={styles.dateFromField ||= {
                flexBasis: 180,
                flexGrow: 1,
                gap: 6
              }}
              testID="timelogsDashboardScreen/dateRangeModal/dateFromField"
            >
              <Text
                style={styles[`dateFromLabel-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase"
                }}
                testID="timelogsDashboardScreen/dateRangeModal/dateFromLabel"
              >
                {_("Date from")}
              </Text>
              <TextInput
                autoCapitalize="none"
                autoCorrect={false}
                maxLength={10}
                onChangeText={this.tt.onDateFromChangeText}
                placeholder="YYYY-MM-DD"
                placeholderTextColor={colors.placeholder}
                style={styles[`dateFromInput-${themeName}`] ||= {
                  backgroundColor: colors.surfaceSunken,
                  borderColor: colors.borderStrong,
                  borderRadius: 10,
                  borderWidth: 1,
                  color: colors.text,
                  fontSize: 15,
                  paddingHorizontal: 12,
                  paddingVertical: 10
                }}
                testID="timelogsDashboardScreen/dateRangeModal/dateFromInput"
                value={this.s.draftDateFrom}
              />
            </View>
            <View
              style={styles.dateToField ||= {
                flexBasis: 180,
                flexGrow: 1,
                gap: 6
              }}
              testID="timelogsDashboardScreen/dateRangeModal/dateToField"
            >
              <Text
                style={styles[`dateToLabel-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase"
                }}
                testID="timelogsDashboardScreen/dateRangeModal/dateToLabel"
              >
                {_("Date to")}
              </Text>
              <TextInput
                autoCapitalize="none"
                autoCorrect={false}
                maxLength={10}
                onChangeText={this.tt.onDateToChangeText}
                placeholder="YYYY-MM-DD"
                placeholderTextColor={colors.placeholder}
                style={styles[`dateToInput-${themeName}`] ||= {
                  backgroundColor: colors.surfaceSunken,
                  borderColor: colors.borderStrong,
                  borderRadius: 10,
                  borderWidth: 1,
                  color: colors.text,
                  fontSize: 15,
                  paddingHorizontal: 12,
                  paddingVertical: 10
                }}
                testID="timelogsDashboardScreen/dateRangeModal/dateToInput"
                value={this.s.draftDateTo}
              />
            </View>
          </View>
          <View
            style={styles.actions ||= {
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 10,
              justifyContent: "flex-end"
            }}
            testID="timelogsDashboardScreen/dateRangeModal/actions"
          >
            <Pressable
              onPress={this.p.onClose}
              style={styles[`cancelButton-${themeName}`] ||= {
                alignItems: "center",
                backgroundColor: colors.surfaceMuted,
                borderColor: colors.border,
                borderRadius: 10,
                borderWidth: 1,
                flexDirection: "row",
                gap: 8,
                paddingHorizontal: 14,
                paddingVertical: 10
              }}
              testID="timelogsDashboardScreen/dateRangeModal/cancelButton"
            >
              <View
                style={styles.cancelButtonIcon ||= {
                  alignItems: "center",
                  justifyContent: "center",
                  width: 16
                }}
                testID="timelogsDashboardScreen/dateRangeModal/cancelButton/icon"
              >
                <FontAwesome6 color={colors.text} name="xmark" size={14} />
              </View>
              <Text
                style={styles[`cancelButtonText-${themeName}`] ||= {
                  color: colors.text,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID="timelogsDashboardScreen/dateRangeModal/cancelButton/text"
              >
                {_("Cancel")}
              </Text>
            </Pressable>
            <Pressable
              onPress={this.tt.onApplyPress}
              style={styles[`applyButton-${themeName}`] ||= {
                alignItems: "center",
                backgroundColor: colors.primary,
                borderColor: colors.primary,
                borderRadius: 10,
                borderWidth: 1,
                flexDirection: "row",
                gap: 8,
                paddingHorizontal: 14,
                paddingVertical: 10
              }}
              testID="timelogsDashboardScreen/dateRangeModal/applyButton"
            >
              <View
                style={styles.applyButtonIcon ||= {
                  alignItems: "center",
                  justifyContent: "center",
                  width: 16
                }}
                testID="timelogsDashboardScreen/dateRangeModal/applyButton/icon"
              >
                <FontAwesome6 color={colors.onPrimary} name="check" size={14} />
              </View>
              <Text
                style={styles[`applyButtonText-${themeName}`] ||= {
                  color: colors.onPrimary,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID="timelogsDashboardScreen/dateRangeModal/applyButton/text"
              >
                {_("Apply")}
              </Text>
            </Pressable>
          </View>
      </Modal>
    )
  }

  /**
   * Stores the draft start date.
   * @param {string} value Start date text.
   * @returns {void} No return value.
   */
  onDateFromChangeText = (value) => {
    this.s.draftDateFrom = value
  }

  /**
   * Stores the draft end date.
   * @param {string} value End date text.
   * @returns {void} No return value.
   */
  onDateToChangeText = (value) => {
    this.s.draftDateTo = value
  }

  /**
   * Applies the current draft date filters.
   * @returns {void} No return value.
   */
  onApplyPress = () => {
    this.p.onApply({
      dateFrom: this.s.draftDateFrom.trim(),
      dateTo: this.s.draftDateTo.trim()
    })
  }
}

/** @type {NamedExoticComponent<TimelogsDashboardDateRangeModalProps>} */
const TimelogsDashboardDateRangeModalComponent = memo(shapeComponent(TimelogsDashboardDateRangeModal))

export default TimelogsDashboardDateRangeModalComponent
